import React, { useState, useEffect } from "react";
import styles from "./CategoryMenuOverlay.module.css";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  lockBodyScroll,
  unlockBodyScroll,
} from "../../../../utils/scrollUtils";

interface Category {
  id: number;
  name: string;
  url: string;
  children: Category[];
  iconUrl?: string | null;
  categoryImageMenuUrl?: string | null;
  color?: string | null;
}

interface CategoryMenuOverlayProps {
  topLevelCategory: Category;
  onClose: () => void;
}

const CategoryMenuOverlay: React.FC<CategoryMenuOverlayProps> = ({
  topLevelCategory,
  onClose,
}) => {
  const [activeSubCategory, setActiveSubCategory] = useState<Category | null>(
    null
  );

  useEffect(() => {
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, []);

  useEffect(() => {
    // Set the first Level 2 category as active when the topLevelCategory changes
    if (topLevelCategory.children.length > 0) {
      setActiveSubCategory(topLevelCategory.children[0]); // Default to the first child
    }
  }, [topLevelCategory]);
  console.log("ActiveSubcat:", activeSubCategory);
  const handleCategoryClick = (category: Category) => {
    setActiveSubCategory(category); // Set active subcategory on click
  };

  // Render Level 3+ subcategories in separate columns
  const renderSubCategoriesInColumns = (
    subCategories: Category[],
    depth = 3
  ) => {
    return subCategories.map((subCategory) => (
      <div key={subCategory.id} className={styles.subcategoryColumn}>
        <h3 className={styles[`level${depth}`]}>{subCategory.name}</h3>
        <ul className={styles.subcategories}>
          {subCategory.children.map((childCategory) => (
            <li
              key={childCategory.id}
              className={`${styles[`level${depth + 1}`]} ${
                styles.subCategoryItem
              } ${
                activeSubCategory?.id === childCategory.id
                  ? styles.activeSubCategory
                  : ""
              }`}
            >
              <a href={`/c/${childCategory.url}`}>{childCategory.name}</a>
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.menuContainer}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the menu
      >
        {/* Left column for Level 2 categories */}
        <div className={styles.leftColumn}>
          {topLevelCategory.children.map((category) => (
            <div
              key={category.id}
              className={`${styles.categoryItem} ${
                activeSubCategory?.id === category.id ? styles.selected : ""
              }`}
              onClick={() => handleCategoryClick(category)}
              style={
                {
                  "--category-color": category.color || "#fff",
                } as React.CSSProperties
              }
            >
              <div className={styles.nameContainer}>
                {/* CategoryName Block */}
                <div className={styles.categoryName}>{category.name}</div>
              </div>
              {/* ArrowBlock */}
              <div className={styles.arrowSection}>
                <ArrowForwardIosIcon
                  className={styles.arrowIcon}
                  style={{ fontSize: "14px" }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Right column for Level 3+ categories */}
        <div className={styles.rightColumn}>
          {activeSubCategory ? (
            <div className={styles.subcategoriesContainer}>
              {renderSubCategoriesInColumns(activeSubCategory.children)}

              {/* Link to "Ver todo {category.name}" and image */}
              <div className={styles.linkImageContainer}>
                <a
                  href={`/c/${activeSubCategory.url}`}
                  className={styles.viewAllLink}
                >
                  {`Ver todo ${activeSubCategory.name} >>`}
                </a>
                {activeSubCategory.categoryImageMenuUrl && (
                  <img
                    src={`${process.env.REACT_APP_IMAGE_FOLDER}${activeSubCategory.categoryImageMenuUrl}`}
                    alt={`${activeSubCategory.name} banner`}
                    className={styles.categoryImage}
                  />
                )}
              </div>
            </div>
          ) : (
            <p>Select a subcategory on the left</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryMenuOverlay;
